/**
 * DocumentFileInput — dual-mode input for admin document forms.
 *
 * Modes:
 *   - "file": upload a real file from disk. Reads it as base64 and hands the
 *     parent { base64, filename, mime, size_bytes } via onFileChange.
 *   - "url":  paste an external link (Drive, Dropbox, …).
 *
 * The parent form decides how to send it to POST /documents:
 *   file mode → file_base64 + file_filename + file_mime.
 *   url  mode → file_url.
 *
 * Only PDF/Word/PNG/JPG/WebP are accepted by design; videos have their own
 * pipeline elsewhere and never belong in the investor document surface.
 */
import React, { useState } from "react";
import { UploadSimple, Link as LinkIcon } from "@phosphor-icons/react";

const ACCEPTED = ".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp";
const MAX_SIZE_BYTES = 8 * 1024 * 1024;

async function readAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // Strip the leading `data:<mime>;base64,` if present so the server just
      // sees the raw base64 payload files_service expects.
      const s = String(reader.result || "");
      const parts = s.split(",", 2);
      resolve(parts.length === 2 ? parts[1] : s);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export default function DocumentFileInput({
  mode = "file",
  onModeChange,
  url = "",
  onUrlChange,
  file = null,
  onFileChange,
  onError,
  disabled = false,
  testidPrefix = "doc-file-input",
}) {
  const [busy, setBusy] = useState(false);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) { onFileChange?.(null); return; }
    if (f.size > MAX_SIZE_BYTES) {
      onError?.("El archivo supera 8 MB.");
      e.target.value = "";
      return;
    }
    setBusy(true);
    try {
      const base64 = await readAsBase64(f);
      onFileChange?.({
        base64,
        filename: f.name,
        mime: f.type || "application/octet-stream",
        size_bytes: f.size,
      });
    } catch {
      onError?.("No se pudo leer el archivo.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sm:col-span-2" data-testid={testidPrefix}>
      <div className="flex gap-4 mb-2 text-sm">
        <label className="inline-flex items-center gap-1.5 cursor-pointer">
          <input
            type="radio"
            checked={mode === "file"}
            onChange={() => onModeChange?.("file")}
            disabled={disabled}
            data-testid={`${testidPrefix}-mode-file`}
          />
          <UploadSimple size={14} /> Subir archivo
        </label>
        <label className="inline-flex items-center gap-1.5 cursor-pointer">
          <input
            type="radio"
            checked={mode === "url"}
            onChange={() => onModeChange?.("url")}
            disabled={disabled}
            data-testid={`${testidPrefix}-mode-url`}
          />
          <LinkIcon size={14} /> Enlace externo
        </label>
      </div>
      {mode === "file" ? (
        <div>
          <input
            type="file"
            accept={ACCEPTED}
            onChange={handleFile}
            disabled={disabled || busy}
            className="dv-input w-full"
            data-testid={`${testidPrefix}-file`}
          />
          {file && (
            <div className="text-xs text-stone-500 mt-1" data-testid={`${testidPrefix}-file-selected`}>
              {file.filename} · {Math.round(file.size_bytes / 1024)} KB
            </div>
          )}
          <div className="text-[11px] text-stone-400 mt-1">
            PDF, Word, PNG, JPG o WebP · máximo 8 MB.
          </div>
        </div>
      ) : (
        <input
          type="url"
          className="dv-input w-full"
          placeholder="URL del archivo (Drive, Dropbox, etc.)"
          value={url}
          onChange={(e) => onUrlChange?.(e.target.value)}
          disabled={disabled}
          data-testid={`${testidPrefix}-url`}
        />
      )}
    </div>
  );
}
